import { JSXElement, mergeProps } from 'solid-js';
import { Button } from '~/components/ui/Button';
import { Localized } from '~/i18n';
import { ClientSecretData } from '~/api';
import { useQueryClient } from '@tanstack/solid-query';
import { useConfirm } from '~/components/ui/Modal';
import { getRelativeTime } from '~/i18n/utils.ts';
import MaskedSecret from '~/components/MaskedSecret';
import { CLIENT_SECRET_HASH_LENGTH } from '~/consts';
import { useToast } from '~/components/ui/Toast';
import {
  ClientSecretFlowEnum,
  deleteClientSecretWithConfirmation,
  getApiCredentialsTranslationKey
} from '~/pages/Settings/utils.ts';
import { useIntl } from '@cookbook/solid-intl';

type ClientSecretItemProps = {
  clientId: string;
  secret: ClientSecretData;
  onRevoke?: (secretId: string) => void;
  allowRevoke?: boolean;
  flowType?: ClientSecretFlowEnum;
};

export default function ClientSecretItem(
  props: ClientSecretItemProps
): JSXElement {
  const merged = mergeProps(
    {
      allowRevoke: false
    },
    props
  );

  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const toaster = useToast();
  const intl = useIntl();

  return (
    <div class={`flex justify-between`}>
      <div class={`flex flex-col text-fg-muted text-sm`}>
        <MaskedSecret
          secretLength={CLIENT_SECRET_HASH_LENGTH}
          suffix={merged.secret.secretSuffix}
        />
        <div class={`mt-1`}>
          <Localized
            translationKey={getApiCredentialsTranslationKey(
              'client_secret_creation_details'
            )}
            vars={{
              dayCreated: getRelativeTime(merged.secret.createTime)
            }}
          />
        </div>
        <div class={`mt-1`}>
          <Localized
            translationKey={getApiCredentialsTranslationKey(
              'client_secret_last_used_details'
            )}
            vars={{
              momentsAgo: getRelativeTime(merged.secret.lastUsedTime)
            }}
          />
        </div>
      </div>
      <Button
        variant={`outline`}
        size={`sm`}
        class={`text-error bg-bg-subtle hover:bg-bg-emphasized`}
        onClick={() => {
          deleteClientSecretWithConfirmation({
            clientId: merged.clientId,
            secretId: merged.secret.id,
            confirm,
            toaster,
            queryClient,
            intl,
            flowType: merged.flowType
          });
        }}
        disabled={!merged.allowRevoke}
        data-testid={`settings-client-secret-delete-modal`}
      >
        <Localized translationKey={`cta_labels.delete`} />
      </Button>
    </div>
  );
}
